import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { analytics } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Users, Mail, TrendingUp, MousePointerClick, ArrowLeft,
  Search, ChevronLeft, ChevronRight, Download,
} from "lucide-react";

const TABS = [
  { key: "opened",      label: "Opened",      icon: TrendingUp,        accent: "text-blue-600 bg-blue-50 border-blue-200",  activeBar: "bg-blue-600" },
  { key: "clicked",     label: "Clicked",      icon: MousePointerClick, accent: "text-purple-600 bg-purple-50 border-purple-200", activeBar: "bg-purple-600" },
  { key: "sent",        label: "Sent To",      icon: Mail,              accent: "text-coral bg-coral/10 border-coral/20",    activeBar: "bg-coral" },
  { key: "subscribers", label: "Subscribers",  icon: Users,             accent: "text-teal bg-teal/10 border-teal/20",       activeBar: "bg-teal" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const TIER_COLORS: Record<string, string> = {
  hot:  "bg-red-100 text-red-700",
  warm: "bg-amber-100 text-amber-700",
  cold: "bg-blue-100 text-blue-700",
  new:  "bg-slate-100 text-slate-600",
};

export default function EngagementDrilldownPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = (sp.get("type") || "opened") as TabKey;
  const campaignFilter = sp.get("campaign_id") || "";

  const [data, setData] = useState<Awaited<ReturnType<typeof analytics.engagementDrilldown>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  const load = async () => {
    setLoading(true);
    try {
      const res = await analytics.engagementDrilldown({
        type: activeTab,
        campaign_id: campaignFilter || undefined,
        page,
        limit,
        search: search || undefined,
      });
      setData(res);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { setPage(1); }, [activeTab, campaignFilter, search]);
  useEffect(() => { load(); }, [activeTab, campaignFilter, page, search]);

  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(sp);
    next.set("type", key);
    if (key === "subscribers") next.delete("campaign_id");
    setSp(next);
  };

  const setCampaign = (id: string) => {
    const next = new URLSearchParams(sp);
    if (id) next.set("campaign_id", id); else next.delete("campaign_id");
    setSp(next);
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 1;
  const tab = TABS.find((t) => t.key === activeTab)!;

  const exportCsv = () => {
    if (!data?.subscribers.length) return;
    const headers = activeTab === "subscribers"
      ? ["Email", "Name", "Status", "Tier", "Subscribed"]
      : ["Email", "Name", "Campaign", "Times", "First", "Last", "Tier"];
    const rows = data.subscribers.map((s) =>
      activeTab === "subscribers"
        ? [s.email, s.name || "", s.status, s.engagement_tier, s.created_at || ""]
        : [s.email, s.name || "", s.campaign_name || "", String(s.event_count || 0), s.first_event || "", s.last_event || "", s.engagement_tier]
    );
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${activeTab}-subscribers.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-2 rounded-xl border border-black/[0.06] text-slate hover:text-ink hover:border-black/[0.12] transition-colors">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-ink">Engagement Details</h1>
            <p className="text-xs sm:text-sm text-slate mt-0.5">See exactly who opened, clicked, or received your emails</p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5">

        {/* Tab pills */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = t.key === activeTab;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  active
                    ? `${t.accent} shadow-sm`
                    : "bg-white text-slate border-black/[0.06] hover:border-black/[0.12] hover:text-ink"
                }`}
              >
                <t.icon size={15} />
                {t.label}
                {data && active && (
                  <span className="ml-1 text-xs opacity-70">({data.total.toLocaleString()})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters row */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate/50" />
            <input
              type="text" placeholder="Search by email or name…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/[0.06] bg-white text-sm text-ink placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/40 transition"
            />
          </div>

          {/* Campaign filter — only for event-based tabs */}
          {activeTab !== "subscribers" && data?.campaigns && data.campaigns.length > 0 && (
            <select
              value={campaignFilter} onChange={(e) => setCampaign(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-black/[0.06] bg-white text-sm text-ink focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/40 transition"
            >
              <option value="">All campaigns</option>
              {data.campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          {/* Export */}
          <button onClick={exportCsv} disabled={!data?.subscribers.length}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-black/[0.06] bg-white text-sm font-semibold text-slate hover:text-ink hover:border-black/[0.12] transition disabled:opacity-40"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
          {loading ? (
            <Spinner className="h-64" />
          ) : !data?.subscribers.length ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate gap-3">
              <tab.icon size={32} className="opacity-20" />
              <p className="text-sm">No {activeTab === "subscribers" ? "active subscribers" : `${activeTab} events`} found</p>
            </div>
          ) : (
            <>
              {/* Desktop */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/[0.06]">
                      <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Email</th>
                      <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Name</th>
                      {activeTab !== "subscribers" && (
                        <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Campaign</th>
                      )}
                      {activeTab !== "subscribers" && (
                        <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Times</th>
                      )}
                      <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Tier</th>
                      <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">
                        {activeTab === "subscribers" ? "Subscribed" : "Last Event"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.subscribers.map((s, i) => {
                      const canClick = activeTab !== "subscribers" && s.campaign_id;
                      return (
                      <tr key={`${s.id}-${s.campaign_id || i}`}
                        onClick={canClick ? () => navigate(`/campaigns/${s.campaign_id}/send-log?search=${encodeURIComponent(s.email)}`) : undefined}
                        className={`border-b border-black/[0.04] transition-colors ${canClick ? "cursor-pointer hover:bg-coral/[0.03]" : "hover:bg-black/[0.01]"}`}
                      >
                        <td className="px-5 py-3.5 font-medium text-ink max-w-[260px] truncate">{s.email}</td>
                        <td className="px-5 py-3.5 text-slate">{s.name || "—"}</td>
                        {activeTab !== "subscribers" && (
                          <td className="px-5 py-3.5 text-slate max-w-[200px] truncate">{s.campaign_name || "—"}</td>
                        )}
                        {activeTab !== "subscribers" && (
                          <td className="px-5 py-3.5 text-ink font-semibold">{s.event_count || 1}</td>
                        )}
                        <td className="px-5 py-3.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${TIER_COLORS[s.engagement_tier] || TIER_COLORS.new}`}>
                            {s.engagement_tier}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-slate text-xs whitespace-nowrap">
                          {new Date(activeTab === "subscribers" ? s.created_at! : s.last_event!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="sm:hidden divide-y divide-black/[0.04]">
                {data.subscribers.map((s, i) => {
                  const canClick = activeTab !== "subscribers" && s.campaign_id;
                  return (
                  <div key={`${s.id}-${s.campaign_id || i}`}
                    onClick={canClick ? () => navigate(`/campaigns/${s.campaign_id}/send-log?search=${encodeURIComponent(s.email)}`) : undefined}
                    className={`px-4 py-3.5 ${canClick ? "cursor-pointer active:bg-coral/[0.04]" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-ink truncate">{s.email}</p>
                        <p className="text-xs text-slate mt-0.5">{s.name || "No name"}</p>
                      </div>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${TIER_COLORS[s.engagement_tier] || TIER_COLORS.new}`}>
                        {s.engagement_tier}
                      </span>
                    </div>
                    {activeTab !== "subscribers" && s.campaign_name && (
                      <p className="text-xs text-slate mt-1 truncate">Campaign: {s.campaign_name}</p>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-black/[0.06]">
                  <p className="text-xs text-slate">
                    Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, data.total)} of {data.total.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPage(page - 1)} disabled={page <= 1}
                      className="p-2 rounded-lg border border-black/[0.06] text-slate hover:text-ink disabled:opacity-30 transition">
                      <ChevronLeft size={14} />
                    </button>
                    <span className="px-3 text-xs font-semibold text-ink">{page} / {totalPages}</span>
                    <button onClick={() => setPage(page + 1)} disabled={page >= totalPages}
                      className="p-2 rounded-lg border border-black/[0.06] text-slate hover:text-ink disabled:opacity-30 transition">
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
